import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import {
  getAgentRules,
  listAgentTokens,
  putAgentRules,
  type AgentRules,
  type AgentToken,
  type TokenCap,
  type TokenRef,
} from './api';

type TokenMode = AgentRules['token_mode'];

const ALL_CHAINS = [
  'ethereum',
  'base',
  'arbitrum',
  'optimism',
  'polygon',
  'bitcoin',
  'solana',
  'tron',
  'cosmos',
  'near',
  'aptos',
  'stellar',
];

export function PoliciesRulesPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const rules = useQuery({
    queryKey: ['agent-rules', orgId],
    queryFn: () => getAgentRules(orgId),
    enabled: !!orgId,
  });

  const tokens = useQuery({
    queryKey: ['agent-tokens', orgId],
    queryFn: () => listAgentTokens(orgId),
    enabled: !!orgId,
  });

  // Local form state — hydrated from the server once.
  const [blockedChains, setBlockedChains] = useState<Set<string>>(new Set());
  const [blockedRecipientsInput, setBlockedRecipientsInput] = useState('');
  const [tokenMode, setTokenMode] = useState<TokenMode>('allow_all');
  const [blockedTokenKeys, setBlockedTokenKeys] = useState<Set<string>>(new Set());
  const [allowedTokenKeys, setAllowedTokenKeys] = useState<Set<string>>(new Set());
  const [maxNativePerTxEth, setMaxNativePerTxEth] = useState('');
  const [maxNativeTotalEth, setMaxNativeTotalEth] = useState('');
  const [tokenCaps, setTokenCaps] = useState<Record<string, TokenCap>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!rules.data) return;
    setBlockedChains(new Set(rules.data.blocked_chains.map((c) => c.toLowerCase())));
    setBlockedRecipientsInput(rules.data.blocked_recipients.join('\n'));
    setTokenMode(rules.data.token_mode);
    setBlockedTokenKeys(new Set(rules.data.blocked_tokens.map(tokenKey)));
    setAllowedTokenKeys(new Set(rules.data.allowed_tokens.map(tokenKey)));
    setMaxNativePerTxEth(rules.data.max_native_per_tx_cap ? weiToEth(rules.data.max_native_per_tx_cap) : '');
    setMaxNativeTotalEth(rules.data.max_native_total_cap ? weiToEth(rules.data.max_native_total_cap) : '');
    setTokenCaps(rules.data.token_caps ?? {});
  }, [rules.data]);

  const save = useMutation({
    mutationFn: () => {
      const refs = (set: Set<string>): TokenRef[] =>
        Array.from(set).map((k) => {
          const [chain, address] = k.split(':');
          return { chain, address };
        });
      const blockedRecipients = blockedRecipientsInput
        .split(/[,\n]/)
        .map((r) => r.trim())
        .filter(Boolean);
      const body: AgentRules = {
        blocked_chains: Array.from(blockedChains),
        blocked_recipients: blockedRecipients,
        token_mode: tokenMode,
        blocked_tokens: refs(blockedTokenKeys),
        allowed_tokens: refs(allowedTokenKeys),
        max_native_per_tx_cap: maxNativePerTxEth ? ethToWei(maxNativePerTxEth) : undefined,
        max_native_total_cap: maxNativeTotalEth ? ethToWei(maxNativeTotalEth) : undefined,
        token_caps: tokenCaps,
      };
      return putAgentRules(orgId, body);
    },
    onSuccess: async () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await qc.invalidateQueries({ queryKey: ['agent-rules', orgId] });
    },
    onError: (e: unknown) => setError(extractError(e)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  function toggleSet(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  }

  function setCap(key: string, field: 'max_per_tx' | 'max_total', value: string) {
    setTokenCaps((prev) => {
      const next = { ...prev };
      const current = next[key] ?? {};
      if (value) next[key] = { ...current, [field]: value };
      else {
        const c = { ...current };
        delete c[field];
        if (!c.max_per_tx && !c.max_total) delete next[key];
        else next[key] = c;
      }
      return next;
    });
  }

  if (!rules.data || !tokens.data) return <p className="text-sm text-ink-tertiary">Loading…</p>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader
          title="What are global rules?"
          description="Constraints that apply on top of every agent session in this organisation. If global rules and the session disagree, the stricter side wins. Useful for hard ceilings ('no agent can ever spend more than X') and security gates ('no agent can ever touch this chain or token')."
        />
      </Card>

      <Card>
        <CardHeader title="Chains & recipients" />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">
              Blocked chains
            </label>
            <p className="mt-1 text-xs text-ink-tertiary">
              Select chains your agents are not allowed to sign on, regardless of what each session permits.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {ALL_CHAINS.map((c) => {
                const on = blockedChains.has(c);
                return (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setBlockedChains((prev) => toggleSet(prev, c))}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      on
                        ? 'border-danger bg-danger/10 text-danger'
                        : 'border-line text-ink-secondary hover:border-danger'
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">
              Blocked recipients (one address per line)
            </label>
            <textarea
              value={blockedRecipientsInput}
              onChange={(e) => setBlockedRecipientsInput(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-line bg-surface-white px-3 py-2 font-mono text-xs text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
              placeholder="0x..."
            />
            <p className="mt-1 text-xs text-ink-tertiary">
              Applies to both native and token transfers — the recipient of the actual transfer is checked, not the contract address.
            </p>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Native spend caps" description="Hard ceilings on native value. The smaller of the session's own limit and this cap wins." />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Max native per tx"
            placeholder="0.5"
            value={maxNativePerTxEth}
            onChange={(e) => setMaxNativePerTxEth(e.target.value)}
          />
          <Input
            label="Max native total per session"
            placeholder="5"
            value={maxNativeTotalEth}
            onChange={(e) => setMaxNativeTotalEth(e.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Token policy"
          description="Mode applies to every token transfer recognised against the token registry. 'Allow all' means tokens are unrestricted at the org level (the session still gates them). 'Deny' blocks a list. 'Allow only' rejects anything not in the list."
        />
        <CardBody className="space-y-4">
          <div className="flex gap-2">
            {(['allow_all', 'deny', 'allow_only'] as TokenMode[]).map((m) => (
              <button
                type="button"
                key={m}
                onClick={() => setTokenMode(m)}
                className={`rounded-md border px-3 py-1.5 text-xs ${
                  tokenMode === m
                    ? 'border-bit2me bg-bit2me/10 text-bit2me'
                    : 'border-line text-ink-secondary hover:border-bit2me'
                }`}
              >
                {m.replace('_', ' ')}
              </button>
            ))}
          </div>

          {(tokenMode === 'deny' || tokenMode === 'allow_only') && (
            <TokenPicker
              tokens={tokens.data}
              selected={tokenMode === 'deny' ? blockedTokenKeys : allowedTokenKeys}
              onChange={(keys) => {
                if (tokenMode === 'deny') setBlockedTokenKeys(keys);
                else setAllowedTokenKeys(keys);
              }}
              toggle={(key) =>
                tokenMode === 'deny'
                  ? setBlockedTokenKeys((s) => toggleSet(s, key))
                  : setAllowedTokenKeys((s) => toggleSet(s, key))
              }
            />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Per-token caps" description="Hard ceilings per token. Amounts are in the token's base units — for a 6-decimal token, 1.50 USDC is 1500000." />
        <CardBody className="space-y-6">
          {tokens.data.length === 0 && (
            <p className="text-sm text-ink-tertiary">No tokens in the registry yet.</p>
          )}
          {groupByChain(tokens.data).map(([chain, list]) => (
            <div key={chain}>
              <h3 className="mb-2 text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">
                {chain}
              </h3>
              <div className="overflow-hidden rounded-md border border-line-soft">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-line-soft bg-surface-soft/40 text-xs uppercase tracking-eyebrow text-ink-tertiary">
                    <tr>
                      <th className="px-3 py-1.5 font-medium">Token</th>
                      <th className="px-3 py-1.5 font-medium">Max per tx</th>
                      <th className="px-3 py-1.5 font-medium">Max total</th>
                      <th className="px-3 py-1.5 font-medium text-right">Decimals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((t) => {
                      const key = `${t.chain}:${t.address.toLowerCase()}`;
                      const cap = tokenCaps[key] ?? {};
                      return (
                        <tr key={t.id} className="border-t border-line-soft">
                          <td className="px-3 py-1.5">
                            <span className="font-medium text-ink-primary">{t.symbol}</span>
                            <span className="ml-2 text-xs text-ink-tertiary">{t.name}</span>
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              value={cap.max_per_tx ?? ''}
                              onChange={(e) => setCap(key, 'max_per_tx', e.target.value)}
                              placeholder="no limit"
                              className="font-mono text-xs"
                            />
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              value={cap.max_total ?? ''}
                              onChange={(e) => setCap(key, 'max_total', e.target.value)}
                              placeholder="no limit"
                              className="font-mono text-xs"
                            />
                          </td>
                          <td className="px-3 py-1.5 text-right text-xs text-ink-tertiary">{t.decimals}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </CardBody>
      </Card>

      {error && (
        <div className="rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {saved && <span className="text-sm text-ok">Saved.</span>}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save rules'}
        </Button>
      </div>
    </form>
  );
}

function TokenPicker({
  tokens,
  selected,
  toggle,
}: {
  tokens: AgentToken[];
  selected: Set<string>;
  onChange: (keys: Set<string>) => void;
  toggle: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {tokens.map((t) => {
        const key = `${t.chain}:${t.address.toLowerCase()}`;
        const on = selected.has(key);
        return (
          <button
            type="button"
            key={t.id}
            onClick={() => toggle(key)}
            className={`rounded-full border px-3 py-1 text-xs ${
              on
                ? 'border-bit2me bg-bit2me/10 text-bit2me'
                : 'border-line text-ink-secondary hover:border-bit2me'
            }`}
          >
            {t.symbol} <span className="text-[10px] opacity-70">· {t.chain}</span>
          </button>
        );
      })}
    </div>
  );
}

function tokenKey(r: TokenRef): string {
  return `${r.chain.toLowerCase()}:${r.address.toLowerCase()}`;
}

function groupByChain(tokens: AgentToken[]): Array<[string, AgentToken[]]> {
  const buckets = new Map<string, AgentToken[]>();
  for (const t of tokens) {
    const list = buckets.get(t.chain) ?? [];
    list.push(t);
    buckets.set(t.chain, list);
  }
  // Sort chains by ALL_CHAINS order, with any unknown chain trailing alphabetically.
  return Array.from(buckets.entries()).sort(([a], [b]) => {
    const ia = ALL_CHAINS.indexOf(a);
    const ib = ALL_CHAINS.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

function ethToWei(s: string): string {
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '000000000000000000').slice(0, 18);
  const wei = BigInt(whole || '0') * 10n ** 18n + BigInt(padded || '0');
  return wei.toString();
}

function weiToEth(wei: string): string {
  try {
    const n = BigInt(wei);
    const whole = n / 10n ** 18n;
    const frac = (n % 10n ** 18n).toString().padStart(18, '0').replace(/0+$/, '');
    return frac ? `${whole}.${frac}` : whole.toString();
  } catch {
    return wei;
  }
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { error?: { message?: string; code?: string } } } }).response;
    if (resp?.data?.error) return resp.data.error.message ?? resp.data.error.code ?? 'unknown error';
  }
  return e instanceof Error ? e.message : 'unknown error';
}
